import { c as _c } from "react/compiler-runtime";
import { SelectManagerDialog } from "@components/table/members/remove-xg-seat/select-manager-dialog";
import { useAddManagerToGroups } from "@components/table/members/remove-xg-seat/use-add-manager-to-groups";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { Checkbox, useToast } from "@flat-ui";
import { getProjectByIdSelector } from "@store/projects/projects-selector";
import { changeProjectManager, fetchProjectsByIds } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { isProjectDemo } from "@utils/project-utils";
import { getPrettyName } from "@utils/user-utils";
import { useEffect, useState } from "react";

/**
 * Returns whether the project is a demo project, whose manager can only be an admin in the workspace, or
 * undefined while that is still being looked up. A project that could not be looked up counts as
 * a regular project, and so does no project at all.
 */
function useIsDemoProject(projectId) {
  const $ = _c(9);
  const dispatch = useAppDispatch();
  let t0;
  if ($[0] !== projectId) {
    t0 = getProjectByIdSelector(projectId);
    $[0] = projectId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const storedProject = useAppSelector(t0);
  const [isLookedUp, setIsLookedUp] = useState(!projectId || !!storedProject);
  let t1;
  let t2;
  if ($[2] !== dispatch || $[3] !== isLookedUp || $[4] !== projectId) {
    t1 = () => {
      if (isLookedUp || !projectId) {
        return;
      }
      const request = dispatch(fetchProjectsByIds({
        projectIds: [projectId]
      }));
      request.finally(() => setIsLookedUp(true));
      return () => request.abort();
    };
    t2 = [dispatch, projectId, isLookedUp];
    $[2] = dispatch;
    $[3] = isLookedUp;
    $[4] = projectId;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  if (storedProject) {
    let t3;
    if ($[7] !== storedProject) {
      t3 = isProjectDemo(storedProject);
      $[7] = storedProject;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  return isLookedUp ? false : undefined;
}
/**
 * Dialog that asks for a new manager of the projects the member manages, since a project manager
 * cannot be removed from a project or be made a viewer. The projects stay in their groups. The user
 * also chooses whether the member stays in the projects as a viewer, which they do unless the user
 * unchecks it.
 *
 * The new manager is picked from the members of the workspace holding a Sphere XG seat, or from
 * its admins when it is a single demo project. A new manager who does not belong to the group of a
 * project yet is added to it as a project manager first.
 */
export function AssignProjectManagerDialog({
  member,
  projects,
  onClose,
  onAssigned
}) {
  const dispatch = useAppDispatch();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    openToast
  } = useToast();
  const singleProject = projects.length === 1 ? projects[0].project : undefined;
  const isDemo = useIsDemoProject(singleProject?.id);
  const addManagerToGroups = useAddManagerToGroups();
  const [shouldKeepAsViewer, setShouldKeepAsViewer] = useState(true);
  const name = <var>{getPrettyName(member)}</var>;
  return <SelectManagerDialog member={member} title="Assign a new project manager" description={singleProject ? <>
            <strong>{name}</strong> manages{" "}
            <strong>
              <var>{singleProject.name}</var>
            </strong>
            . Choose the new project manager first.
          </> : <>
            <strong>{name}</strong> manages <var>{projects.length}</var>{" "}
            projects. Choose another member to manage all of these projects. If
            the selected member is not part of the groups where the projects
            belong to, it will be added as a project manager of the group.
          </>} dataTestId="assign-project-manager-dialog" isAdminOnly={!!isDemo} areCandidatesLoading={isDemo === undefined} onClose={onClose} onAssign={assignManager}>
      <Checkbox checked={shouldKeepAsViewer} onChange={() => setShouldKeepAsViewer(isChecked => !isChecked)} label={singleProject ? <>Keep {name} in the project as a viewer</> : <>Keep {name} in these projects as a viewer</>} />
    </SelectManagerDialog>;

  /**
   * Adds the new manager to the groups of the projects they do not belong to yet, then makes them
   * the manager of the projects one after the other, and tells the user once that worked. A
   * project whose manager could not be changed is told by an error toast.
   *
   * @returns whether that worked for any of the projects
   */
  async function assignManager(newManager) {
    const joinedGroupIds = new Set(await addManagerToGroups(newManager, [...new Set(projects.map(({
      group
    }) => group.id))]));
    const assignedProjects = [];
    for (const memberProject of projects) {
      if (joinedGroupIds.has(memberProject.group.id) && (await changeManager(memberProject, newManager))) {
        assignedProjects.push(memberProject);
      }
    }
    if (!assignedProjects.length) {
      return false;
    }
    openToast({
      title: "Project manager assigned",
      message: <>
          <strong>
            <var>{getPrettyName(newManager)}</var>
          </strong>{" "}
          now manages{" "}
          {assignedProjects.length === 1 ? <var>{assignedProjects[0].project.name}</var> : <>
              <var>{assignedProjects.length}</var> projects
            </>}
          .
        </>
    });
    onAssigned(shouldKeepAsViewer, assignedProjects);
    return true;
  }

  /**
   * Makes the member the manager of a project
   *
   * @returns whether that worked, telling the user why not otherwise
   */
  async function changeManager({
    project,
    group: group_0
  }, newManager_0) {
    try {
      await dispatch(changeProjectManager({
        projectId: project.id,
        projectManagerIdentity: newManager_0.identity,
        groupId: group_0.id
      })).unwrap();
      return true;
    } catch (error) {
      handleErrorWithToast({
        id: `assignProjectManager-${project.id}-${Date.now().toString()}`,
        title: "Cannot change the project manager",
        error
      });
      return false;
    }
  }
}